import {Fragment} from "react";
import {Table, TableBody, TableCell, TableRow} from "@mui/material";
import FormattedTableHead from "../../../FormattedTableHead";
import FormattedRegistrationTime from "../../../FormattedRegistrationTime";
import CreateButton from "./save/CreateButton";
import DeleteButton from "./DeleteButton";
import UpdateOrInfoButton from "./save/UpdateOrInfoButton";
import PhoneButton from "./phone-buttons/PhoneButton";
import NoDeviceBlock from "../NoDeviceBlock";
import ResyncButton from "./resync/ResyncButton";
import {HasPermission} from "../../../widgets";
import {ProvisionedDevicesQueryHookResult} from "../queries.generated";
import {defineMessage} from "react-intl";
import {ContextFilters} from "@/context/AuthProvider";
import {useDomainNameQuery} from "@/hooks";

type Props = {
    query: ProvisionedDevicesQueryHookResult;
    onMutate: () => void;
    extensionId: number;
    domainId: number;
    canAdd: boolean;
};

export default function Provisioned({query, onMutate, extensionId, domainId, canAdd}: Props) {
    const componentId = "extension-provisioned-devices";
    const {domainName} = useDomainNameQuery(domainId);
    const domainNameContextFilter = ContextFilters.domain(domainName);

    if (!query.data.provisionedDevices.length) {
        return (
            <div id={componentId}>
                <NoDeviceBlock idSuffix="provisioned">
                    <HasPermission name="manage_provisioned_device" contextFilter={domainNameContextFilter}>
                        <CreateButton
                            disabled={!canAdd}
                            extensionId={extensionId}
                            domainId={domainId}
                            onSuccess={onSuccess}
                        />
                    </HasPermission>
                </NoDeviceBlock>
            </div>
        );
    }

    function onSuccess() {
        query.refetch();
        onMutate();
    }

    return (
        <Fragment>
            <Table id={componentId}>
                <FormattedTableHead
                    cells={[
                        {label: defineMessage({id: "mac_address", defaultMessage: "MAC address"})},
                        {label: defineMessage({id: "vendor", defaultMessage: "Vendor"})},
                        {label: defineMessage({id: "variant", defaultMessage: "Variant"})},
                        {label: defineMessage({id: "devices.registrationTime", defaultMessage: "Registered"})},
                        {
                            props: {align: "center"},
                            renderer: () => (
                                <HasPermission name="manage_provisioned_device" contextFilter={domainNameContextFilter}>
                                    <CreateButton
                                        disabled={!canAdd}
                                        extensionId={extensionId}
                                        domainId={domainId}
                                        onSuccess={onSuccess}
                                    />
                                </HasPermission>
                            ),
                        },
                    ]}
                />
                <TableBody>
                    {query.data.provisionedDevices.map((device) => (
                        <TableRow key={device.id}>
                            <TableCell data-path="provisioned-devices.macAddress" data-value={device.macAddress}>
                                {device.macAddress}
                            </TableCell>
                            <TableCell>{device.vendor?.name}</TableCell>
                            <TableCell>{device.variant}</TableCell>
                            <TableCell>
                                <FormattedRegistrationTime
                                    registrationTime={device.registrationTime}
                                    expirationTime={device.registrationExpireTime}
                                    clientInfo={device.clientInfo}
                                />
                            </TableCell>
                            <TableCell align="center">
                                <HasPermission
                                    name="manage_provisioned_device_buttons"
                                    contextFilter={domainNameContextFilter}
                                >
                                    <PhoneButton device={device} domainId={domainId} userId={extensionId} />
                                </HasPermission>
                                <UpdateOrInfoButton onSuccess={query.refetch} device={device} domainId={domainId} />
                                <HasPermission name="resync_device">
                                    <ResyncButton device={device} />
                                </HasPermission>
                                <HasPermission name="manage_provisioned_device" contextFilter={domainNameContextFilter}>
                                    <DeleteButton
                                        id={device.id}
                                        name={`${device.vendor.name} - ${device.variant} MAC: ${device.macAddress}`}
                                        onSuccess={onSuccess}
                                        dataIdentifier={device.macAddress}
                                    />
                                </HasPermission>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </Fragment>
    );
}
